import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "./AllAutovehicles.css";

const DEFAULT_PAGE_SIZE = 10;

const MileageHistory = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [vehicle, setVehicle] = useState(null);
    const [mileageHistory, setMileageHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("mileagehistory");
    const [canView, setCanView] = useState(false);

    useEffect(() => {
        const requiredPath = "/all-autovehicles";
        const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
        const pages = storedPermissions?.pages ?? [];

        const checkPermission = (key) =>
            pages.some((p) => {
                try {
                    return new URL(p.url).pathname === requiredPath && p[key] === true;
                } catch {
                    return false;
                }
            });

        setCanView(checkPermission("can_view"));
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const vehicleRes = await axiosInstance.get(`/all-autovehicles/${id}/`);
            setVehicle(vehicleRes.data);

            const mileageRes = await axiosInstance.get(`/autovehicle-kilometers/?ordering=-recorded_date`);
            const vehicleId = parseInt(id);
            const vehicleMileage = mileageRes.data.filter(item =>
                item.vehicle === vehicleId ||
                (item.vehicle && typeof item.vehicle === 'object' && item.vehicle.id === vehicleId)
            );

            setMileageHistory(vehicleMileage);
        } catch (error) {
            console.error("Error loading mileage history:", error);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('ro-RO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const columns = useMemo(() => [
        {
            field: "recorded_date",
            headerName: "Recorded Date",
            flex: 1.2,
            minWidth: 150,
            renderCell: (params) => (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingLeft: '16px'
                }}>
                    {formatDate(params.value)}
                </div>
            )
        },
        {
            field: "kilometers",
            headerName: "Kilometers",
            flex: 1,
            minWidth: 130,
            renderCell: (params) => (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingLeft: '16px'
                }}>
                    <span style={{ fontWeight: 600 }}>
                        {params.value ? params.value.toLocaleString('ro-RO') : "0"} km
                    </span>
                </div>
            )
        },
        {
            field: "vehicle",
            headerName: "Vehicle",
            flex: 1.5,
            minWidth: 180,
            renderCell: () => (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingLeft: '16px'
                }}>
                    {vehicle ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            gap: '2px',
                            lineHeight: '1.2'
                        }}>
                            <div style={{ 
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#333'
                            }}>
                                {vehicle.auto || "Vehicle"}
                            </div>
                            <div style={{ 
                                fontSize: '0.85rem', 
                                color: '#666',
                                fontWeight: 400
                            }}>
                                {vehicle.registration_number || "No registration"}
                            </div>
                        </div>
                    ) : (
                        <span>N/A</span>
                    )}
                </div>
            )
        }
    ], [vehicle]);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
        if (tab === "details") {
            navigate(`/all-autovehicles/view/${id}`);
        } else if (tab === "contract-information") {
            navigate(`/all-autovehicles/view/${id}/contract-information`);
        } else if (tab === "problems") {
            navigate(`/all-autovehicles/view/${id}/problems`);
        } else {
            if (window.location.pathname !== `/all-autovehicles/view/${id}/mileagehistory`) {
                navigate(`/all-autovehicles/view/${id}/mileagehistory`);
            }
        }
    };

    if (loading) return <div className="loading">Loading...</div>;

    if (!canView) {
        return (
            <div style={{ padding: "40px", textAlign: "center", fontSize: "18px" }}>
                ❌ You do not have permission to view this page.
            </div>
        );
    }

    return (
        <div className="addautovehicles-page">
            <div className="add-autovehicle-container">

                <div className="top-header addauto-header">
                    <div className="header-left">
                        <ArrowBackIcon
                            className="back-icon-autovehicles"
                            onClick={() => navigate("/all-autovehicles")}
                        />
                        <h2 className="header-title-autovehicles">View autovehicle</h2>
                    </div>
                </div>

                <hr className="header-divider-autovehicles" />

                <div className="tab-buttons-autovehicles">
                    <button
                        className={`tab-btn ${activeTab === "details" ? "active" : ""}`}
                        onClick={() => handleTabClick("details")}
                    >
                        Details
                    </button>
                    <button
                        className={`tab-btn ${activeTab === "contract-information" ? "active" : ""}`}
                        onClick={() => handleTabClick("contract-information")}
                    >
                        Contract Information
                    </button>
                    <button
                        className={`tab-btn ${activeTab === "mileagehistory" ? "active" : ""}`}
                        onClick={() => handleTabClick("mileagehistory")}
                    >
                        Mileage History
                    </button>
                    <button
                        className={`tab-btn ${activeTab === "problems" ? "active" : ""}`}
                        onClick={() => handleTabClick("problems")}
                    >
                        Problems
                    </button>
                </div>

                <div className="table-container" style={{ marginTop: "20px" }}>
                    <DataGrid
                        rows={mileageHistory}
                        columns={columns}
                        loading={loading}
                        disableRowSelectionOnClick
                        pageSizeOptions={[5, 10, 20]}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: DEFAULT_PAGE_SIZE, page: 0 },
                            },
                        }}
                        rowHeight={60} // Crește înălțimea rândului pentru a încăpea textul pe două linii
                        sx={{
                            borderRadius: "8px",
                            background: "white",
                            border: "1px solid #e0e0e0",
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                                padding: "0 !important",
                                overflow: "visible"
                            },
                            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: '#f5f5f5',
                                borderBottom: '2px solid #e0e0e0',
                                "& .MuiDataGrid-columnHeaderTitleContainer": {
                                    justifyContent: "flex-start",
                                    paddingLeft: "16px",
                                }
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: 600,
                            },
                            "& .MuiDataGrid-row:nth-of-type(even)": {
                                backgroundColor: '#fafafa',
                                '&:hover': {
                                    backgroundColor: '#f0f0f0',
                                }
                            },
                            "& .MuiDataGrid-row:nth-of-type(odd)": {
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                }
                            },
                            "& .MuiDataGrid-virtualScroller": {
                                overflow: "visible !important"
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MileageHistory;